import React, { useMemo } from "react";

/**
 * Courbe de loi d'eau (heating curve) for a PAC.
 *
 * X-axis : température extérieure (de t_base à +20°C)
 * Y-axis : température de départ chauffage (de t_interieur à 75°C)
 *
 * Two zones :
 *   - Basse température (≤ 55°C) — yellow/amber gradient
 *   - Moyenne/Haute température (55–75°C) — orange/red gradient
 *
 * The selected emitter is highlighted. Both curves converge at the indoor
 * temperature point (no heating needed when outdoor = indoor).
 */
export default function HeatingCurve({
  t_base = -7,
  t_interieur = 20,
  selectedEmetteur = "basse_temp",
}) {
  // Domain
  const xMin = Math.floor(Math.min(t_base - 2, -15));
  const xMax = Math.ceil(t_interieur);
  const yMin = Math.floor(t_interieur);
  const yMax = 75;

  // SVG dimensions
  const W = 640;
  const H = 360;
  const PAD_L = 50;
  const PAD_R = 20;
  const PAD_T = 22;
  const PAD_B = 50;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xScale = (x) => PAD_L + ((x - xMin) / (xMax - xMin)) * innerW;
  const yScale = (y) => PAD_T + (1 - (y - yMin) / (yMax - yMin)) * innerH;

  // The 3 curves all converge at (t_interieur, t_interieur).
  // At t_base, the water temp reaches the emitter's max.
  // Basse: linear from (t_base, 55) to (t_interieur, t_interieur)
  // Moyenne: from (t_base, 65) to (t_interieur, t_interieur)
  // Haute: from (t_base, 75) to (t_interieur, t_interieur)
  const curve = (yMaxEmitter) => {
    // Slight curve via quadratic for visual finesse
    const points = [];
    const steps = 40;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = xMin + t * (xMax - xMin);
      // Map x in [t_base ... t_interieur] domain for the curve.
      // For x < t_base, extrapolate slightly above max.
      let frac;
      if (x >= t_interieur) frac = 0;
      else if (x <= t_base) frac = 1 + (t_base - x) / 8; // overshoot beyond t_base
      else frac = (t_interieur - x) / (t_interieur - t_base);
      // Add slight concavity
      const concave = Math.pow(frac, 0.85);
      const y = t_interieur + (yMaxEmitter - t_interieur) * concave;
      points.push([x, y]);
    }
    return points;
  };

  const basse = useMemo(() => curve(35), [t_base, t_interieur]);
  const moyenne = useMemo(() => curve(65), [t_base, t_interieur]);
  const haute = useMemo(() => curve(75), [t_base, t_interieur]);

  const pointsToPath = (pts) => "M " + pts.map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ");
  const pointsToAreaPath = (pts, baseY) =>
    "M " + pts.map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ") +
    ` L ${xScale(pts[pts.length - 1][0])},${yScale(baseY)} L ${xScale(pts[0][0])},${yScale(baseY)} Z`;

  const isBasse = selectedEmetteur === "basse_temp" || selectedEmetteur === "plancher_chauffant" || selectedEmetteur === "radiateur_bt";
  const isMH = selectedEmetteur === "moyenne_haute_temp" || selectedEmetteur === "radiateur_ht";

  // X ticks
  const xTicks = [];
  const xStep = xMax - xMin > 25 ? 5 : 2.5;
  for (let v = Math.ceil(xMin / xStep) * xStep; v <= xMax; v += xStep) xTicks.push(v);
  // Y ticks
  const yTicks = [];
  for (let v = yMin; v <= yMax; v += 5) yTicks.push(v);

  // Highlight: the working point for the selected curve at t_base
  const workingPoint = (() => {
    if (isBasse) return { x: t_base, y: 35, label: "35°C" };
    if (isMH) return { x: t_base, y: 70, label: "≈ 70°C" };
    return null;
  })();

  return (
    <div className="pac-card p-5" data-testid="heating-curve">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Courbe de loi d'eau</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Température de départ chauffage en fonction de la température extérieure
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono-data">
          Tbase {t_base}°C
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        data-testid="heating-curve-svg"
      >
        <defs>
          <linearGradient id="grad-basse" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fde68a" stopOpacity={isBasse ? 1 : 0.55} />
            <stop offset="100%" stopColor="#fef3c7" stopOpacity={isBasse ? 1 : 0.4} />
          </linearGradient>
          <linearGradient id="grad-moyenne" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fb923c" stopOpacity={isMH ? 1 : 0.55} />
            <stop offset="100%" stopColor="#fdba74" stopOpacity={isMH ? 1 : 0.4} />
          </linearGradient>
          <linearGradient id="grad-haute" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity={isMH ? 1 : 0.55} />
            <stop offset="100%" stopColor="#f87171" stopOpacity={isMH ? 1 : 0.4} />
          </linearGradient>
        </defs>

        {/* Grid */}
        {yTicks.map((y) => (
          <line
            key={`gy-${y}`}
            x1={PAD_L}
            x2={W - PAD_R}
            y1={yScale(y)}
            y2={yScale(y)}
            stroke="#e2e8f0"
            strokeWidth={0.5}
          />
        ))}
        {xTicks.map((x) => (
          <line
            key={`gx-${x}`}
            y1={PAD_T}
            y2={H - PAD_B}
            x1={xScale(x)}
            x2={xScale(x)}
            stroke="#e2e8f0"
            strokeWidth={0.5}
          />
        ))}

        {/* Zone Haute (red): above moyenne curve up to haute curve */}
        <path
          d={
            "M " +
            haute.map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ") +
            " L " +
            moyenne.slice().reverse().map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ") +
            " Z"
          }
          fill="url(#grad-haute)"
        />
        {/* Zone Moyenne (orange): above basse curve up to moyenne curve */}
        <path
          d={
            "M " +
            moyenne.map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ") +
            " L " +
            basse.slice().reverse().map(([x, y]) => `${xScale(x)},${yScale(y)}`).join(" L ") +
            " Z"
          }
          fill="url(#grad-moyenne)"
        />
        {/* Zone Basse (yellow): from indoor temp baseline up to basse curve */}
        <path d={pointsToAreaPath(basse, yMin)} fill="url(#grad-basse)" />

        {/* Curve outlines (more saturated when selected) */}
        <path d={pointsToPath(haute)} fill="none" stroke="#dc2626" strokeWidth={isMH ? 2 : 1} opacity={isMH ? 0.9 : 0.5} />
        <path d={pointsToPath(moyenne)} fill="none" stroke="#ea580c" strokeWidth={isMH ? 2 : 1} opacity={isMH ? 0.9 : 0.5} />
        <path d={pointsToPath(basse)} fill="none" stroke="#d97706" strokeWidth={isBasse ? 2.5 : 1} opacity={isBasse ? 0.95 : 0.55} />

        {/* Zone labels */}
        <text x={xScale(t_base) + 6} y={yScale(70)} fontSize="11" fontWeight="700" fill="#7f1d1d" className="font-display" style={{ opacity: isMH ? 1 : 0.55 }}>
          Haute T°C  65–75°C
        </text>
        <text x={xScale(t_base) + 6} y={yScale(60)} fontSize="11" fontWeight="700" fill="#7c2d12" className="font-display" style={{ opacity: isMH ? 1 : 0.55 }}>
          Moyenne T°C  55–65°C
        </text>
        <text x={xScale(t_base) + 6} y={yScale(45)} fontSize="11" fontWeight="700" fill="#854d0e" className="font-display" style={{ opacity: isBasse ? 1 : 0.55 }}>
          Basse T°C  35°C
        </text>

        {/* Axis line */}
        <line x1={PAD_L} x2={W - PAD_R} y1={H - PAD_B} y2={H - PAD_B} stroke="#94a3b8" strokeWidth={1} />
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={H - PAD_B} stroke="#94a3b8" strokeWidth={1} />

        {/* X-axis labels */}
        {xTicks.map((x) => (
          <text
            key={`tx-${x}`}
            x={xScale(x)}
            y={H - PAD_B + 16}
            textAnchor="middle"
            fontSize={10}
            fill="#64748b"
            className="font-mono-data"
          >
            {x}°C
          </text>
        ))}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 8} textAnchor="middle" fontSize={11} fill="#475569" fontWeight={600}>
          Température extérieure
        </text>

        {/* Y-axis labels */}
        {yTicks.map((y) => (
          <text
            key={`ty-${y}`}
            x={PAD_L - 8}
            y={yScale(y) + 3}
            textAnchor="end"
            fontSize={10}
            fill="#64748b"
            className="font-mono-data"
          >
            {y}°C
          </text>
        ))}
        <text
          transform={`rotate(-90, 14, ${(PAD_T + H - PAD_B) / 2})`}
          x={14}
          y={(PAD_T + H - PAD_B) / 2}
          textAnchor="middle"
          fontSize={11}
          fill="#475569"
          fontWeight={600}
        >
          Température de départ chauffage
        </text>

        {/* Working point marker at t_base */}
        {workingPoint && (
          <g>
            <line
              x1={xScale(workingPoint.x)}
              x2={xScale(workingPoint.x)}
              y1={PAD_T}
              y2={H - PAD_B}
              stroke="#0f172a"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.5}
            />
            <circle
              cx={xScale(workingPoint.x)}
              cy={yScale(workingPoint.y)}
              r={7}
              fill="#ffffff"
              stroke="#0f172a"
              strokeWidth={2}
              data-testid="curve-working-point"
            />
            <rect
              x={xScale(workingPoint.x) + 10}
              y={yScale(workingPoint.y) - 22}
              width={94}
              height={32}
              rx={6}
              ry={6}
              fill="#0f172a"
            />
            <text
              x={xScale(workingPoint.x) + 57}
              y={yScale(workingPoint.y) - 9}
              textAnchor="middle"
              fontSize={10}
              fill="#cbd5e1"
              className="font-mono-data"
            >
              à {workingPoint.x}°C ext.
            </text>
            <text
              x={xScale(workingPoint.x) + 57}
              y={yScale(workingPoint.y) + 4}
              textAnchor="middle"
              fontSize={12}
              fontWeight="700"
              fill="#ffffff"
              className="font-mono-data"
            >
              {workingPoint.label}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
